import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import './AdminPanel.css';
import { API_URL } from '../config';

function AdminPanel({ onLogout }) {
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('businesses');
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: '',
    businessId: '',
    environment: 'test'
  });

  // New business form state
  const [newBusiness, setNewBusiness] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    emailFrequency: 'Quarterly',
    initialCredits: 100 // Default initial credits
  });

  // New user form state
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'user',
    businessId: '',
    environment: 'test'
  });

  // Add this to your state declarations
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [businessFormData, setBusinessFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    emailFrequency: 'Quarterly',
    availableCredits: 0,
    usedCredits: 0,
    can_run_manual_checks: false
  });

  // Add to existing state declarations
  const [checks, setChecks] = useState([]);

  // Add these new state variables for notification testing
  const [notificationType, setNotificationType] = useState('SYSTEM_UPDATE');
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [notificationResult, setNotificationResult] = useState(null);
  const [notificationLoading, setNotificationLoading] = useState(false);

  // Schedule management state
  const [schedules, setSchedules] = useState([]);
  const [scheduleRuns, setScheduleRuns] = useState([]);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [triggeringSchedules, setTriggeringSchedules] = useState(new Set());
  
  // Run logs state
  const [viewingRunLogs, setViewingRunLogs] = useState(null);
  const [runLogs, setRunLogs] = useState([]);
  const [runDetails, setRunDetails] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  // Live progress tracking for active runs
  const [activeRunsProgress, setActiveRunsProgress] = useState({});

  // Move axiosAuth outside of component or use useMemo
  const axiosAuth = React.useMemo(() => axios.create({
    baseURL: API_URL,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
    }
  }), []); // Empty dependency array as we only need to create this once

  // Ref to track polling interval
  const pollingIntervalRef = useRef(null);

  // Poll for active run progress - separate from initial data load
  useEffect(() => {
    // Only poll if we're on the schedules tab
    if (activeTab !== 'schedules') {
      setActiveRunsProgress({});
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Wait for initial data to load before starting to poll
    if (loading) {
      return;
    }

    const pollActiveRuns = async () => {
      try {
        // Get current runs to check for active ones - ADD TIMESTAMP TO PREVENT CACHING
        const runsRes = await axiosAuth.get(`/api/schedules/admin/recent-runs?limit=50&_t=${Date.now()}`);
        const runs = runsRes.data;
        
        // Debug: Log runs with processed_staff data
        const runningRuns = runs.filter(r => r.status === 'running');
        if (runningRuns.length > 0) {
          console.log('📊 Recent runs data:', runningRuns.map(r => ({
            id: r.id,
            status: r.status,
            total_staff: r.total_staff,
            processed_staff: r.processed_staff,
            successful_checks: r.successful_checks,
            failed_checks: r.failed_checks
          })));
        }
        
        // Always update runs list to keep it fresh (this updates the UI in real-time)
        setScheduleRuns(runs);
        
        // Filter for running/pending runs OR recently completed (within 60 seconds)
        // This ensures we catch completion status changes
        const now = new Date();
        const activeOrRecentRuns = runs.filter(run => {
          // If it's running or pending, include it
          if (run.status === 'running' || (!run.completed_at && run.status !== 'completed' && run.status !== 'failed')) {
            return true;
          }
          
          // If it's completed or failed, check if it happened recently
          if (run.status === 'completed' || run.status === 'failed') {
            // If completed_at exists, check age
            if (run.completed_at) {
              const completedDate = new Date(run.completed_at);
              const ageInSeconds = (now - completedDate) / 1000;
              return ageInSeconds < 60; // Keep polling for 60 seconds after completion to catch status changes
            }
            // If no completed_at but status is completed/failed, check run_date age
            if (run.run_date) {
              const runDate = new Date(run.run_date);
              const ageInSeconds = (now - runDate) / 1000;
              // If completed within last 90 seconds, show it (longer window to catch updates)
              return ageInSeconds < 90;
            }
            // Default: include completed runs for a bit longer to ensure UI updates
            return true;
          }
          
          return false;
        });
        
        // If no active or recent runs, clear progress and stop polling
        if (activeOrRecentRuns.length === 0) {
          console.log('📊 No active or recent runs, stopping polling');
          setActiveRunsProgress({});
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return;
        }
        
        console.log(`📊 Polling ${activeOrRecentRuns.length} active/recent run(s)`);
        
        // Fetch progress for each active or recent run - ADD TIMESTAMP TO PREVENT CACHING
        const progressPromises = activeOrRecentRuns.map(async (run) => {
          try {
            const progressRes = await axiosAuth.get(`/api/schedules/admin/runs/${run.id}/progress?_t=${Date.now()}`);
            console.log(`📡 Progress endpoint response for run ${run.id}:`, {
              processed_staff: progressRes.data.processed_staff,
              total_staff: progressRes.data.total_staff,
              status: progressRes.data.status,
              fullData: progressRes.data
            });
            return { runId: run.id, progress: progressRes.data };
          } catch (error) {
            console.error(`❌ Error fetching progress for run ${run.id}:`, error);
            return null;
          }
        });
        
        const progressResults = await Promise.all(progressPromises);
        const progressMap = {};
        let hasStatusChange = false;
        
        progressResults.forEach(result => {
          if (result) {
            const oldProgress = activeRunsProgress[result.runId];
            const newStatus = result.progress.status;
            const oldStatus = oldProgress?.status;
            
            // Detect status changes (especially completion)
            if (oldStatus && oldStatus !== newStatus) {
              console.log(`🔄 Status change detected for run ${result.runId}: ${oldStatus} → ${newStatus}`);
              hasStatusChange = true;
            }
            
            progressMap[result.runId] = result.progress;
            console.log(`📦 Stored progress for run ${result.runId}:`, {
              processed_staff: result.progress.processed_staff,
              total_staff: result.progress.total_staff,
              status: result.progress.status,
              isComplete: result.progress.isComplete
            });
          }
        });
        
        // Force state update if status changed (ensures UI re-renders)
        setActiveRunsProgress(prev => {
          // If status changed, create new object to force React re-render
          if (hasStatusChange) {
            console.log('🔄 Forcing UI update due to status change');
            return { ...progressMap };
          }
          return progressMap;
        });
        
        // If any run completed, also refresh the runs list to get latest status
        if (hasStatusChange) {
          console.log('🔄 Status change detected, refreshing runs list...');
          // Small delay to ensure backend has committed the status change
          setTimeout(async () => {
            try {
              const freshRunsRes = await axiosAuth.get(`/api/schedules/admin/recent-runs?limit=50&_t=${Date.now()}`);
              setScheduleRuns(freshRunsRes.data);
            } catch (error) {
              console.error('Error refreshing runs list:', error);
            }
          }, 500);
        }
      } catch (error) {
        console.error('Error polling active runs:', error);
      }
    };
    
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Start polling after a short delay to let initial load complete
    const startPolling = setTimeout(() => {
      pollActiveRuns(); // Poll once immediately
      
      // Then set up interval
      pollingIntervalRef.current = setInterval(pollActiveRuns, 2000);
    }, 1000); // Reduced delay for faster feedback
    
    return () => {
      clearTimeout(startPolling);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, axiosAuth, loading]);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      if (!mounted) return;
      
      setLoading(true);
      try {
        const [businessesRes, usersRes, checksRes, schedulesRes, runsRes] = await Promise.all([
          axiosAuth.get('/api/admin/businesses'),
          axiosAuth.get('/api/admin/users'),
          axiosAuth.get('/api/admin/checks'),
          axiosAuth.get('/api/schedules/admin/all'),
          axiosAuth.get('/api/schedules/admin/recent-runs?limit=50')
        ]);

        if (mounted) {
          setBusinesses(businessesRes.data);
          setUsers(usersRes.data);
          setChecks(checksRes.data);
          setSchedules(schedulesRes.data);
          setScheduleRuns(runsRes.data);
          setLoading(false);
        }
      } catch (err) {
        if (mounted) {
          console.error('Failed to load admin data:', err);
          toast.error('Failed to load data: ' + (err.response?.data?.error || err.message));
          setLoading(false);
          if (err.response?.status === 401) {
            onLogout();
          }
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      mounted = false;
    };
  }, [axiosAuth, onLogout]); // Minimal dependencies

  // Fetch staff when editing schedule
  useEffect(() => {
    const fetchStaff = async () => {
      if (!editingSchedule) {
        setAvailableStaff([]);
        return;
      }

      setLoadingStaff(true);
      try {
        const response = await axiosAuth.get(
          `/api/admin/businesses/${editingSchedule.business_id}/staff?environment=${editingSchedule.environment}`
        );
        setAvailableStaff(response.data);
        
        // Parse existing excluded_staff_ids if not already parsed - use functional update
        if (editingSchedule.excluded_staff_ids && typeof editingSchedule.excluded_staff_ids === 'string') {
          try {
            const parsed = JSON.parse(editingSchedule.excluded_staff_ids);
            setEditingSchedule(prev => ({
              ...prev,
              excluded_staff_ids: parsed
            }));
          } catch (e) {
            setEditingSchedule(prev => ({
              ...prev,
              excluded_staff_ids: []
            }));
          }
        } else if (!editingSchedule.excluded_staff_ids) {
          setEditingSchedule(prev => ({
            ...prev,
            excluded_staff_ids: []
          }));
        }
      } catch (error) {
        console.error('Failed to fetch staff:', error);
        toast.error('Failed to load staff list');
      } finally {
        setLoadingStaff(false);
      }
    };

    fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingSchedule?.id, editingSchedule?.business_id, editingSchedule?.environment, axiosAuth]); // Only refetch when these specific properties change, not the entire object

  const handleAddBusiness = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosAuth.post('/api/admin/businesses', {
        name: newBusiness.name,
        email: newBusiness.email,
        phone: newBusiness.phone,
        address: newBusiness.address,
        emailFrequency: newBusiness.emailFrequency,
        available_credits: newBusiness.initialCredits,
        used_credits: 0
      });
      
      setBusinesses([...businesses, response.data]);
      setNewBusiness({
        name: '',
        email: '',
        phone: '',
        address: '',
        emailFrequency: 'Quarterly',
        initialCredits: 100
      });
    } catch (err) {
      toast.error('Failed to add business');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosAuth.post('/api/admin/users', newUser);
      setUsers([...users, response.data]);
      setNewUser({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'user',
        businessId: '',
        environment: 'test'
      });
    } catch (err) {
      toast.error('Failed to add user');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axiosAuth.delete(`/api/admin/users/${userId}`);
        setUsers(users.filter(user => user.id !== userId));
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      firstName: user.first_name,
      lastName: user.last_name,
      role: user.role,
      businessId: user.business_id,
      environment: user.environment || 'test'
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosAuth.put(`/api/admin/users/${editingUser.id}`, formData);
      setUsers(users.map(user => 
        user.id === editingUser.id ? response.data : user
      ));
      setEditingUser(null);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: '',
        businessId: '',
        environment: 'test'
      });
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Function to view detailed logs for a run
  const handleViewLogs = async (runId) => {
    try {
      setLoadingLogs(true);
      setViewingRunLogs(runId);
      
      const response = await axiosAuth.get(`/api/schedules/admin/runs/${runId}/logs`);
      setRunDetails(response.data.run);
      setRunLogs(response.data.logs);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load logs');
      setViewingRunLogs(null);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (window.confirm('Are you sure you want to delete this business? This will also delete all associated users.')) {
      try {
        await axiosAuth.delete(`/api/admin/businesses/${businessId}`);
        setBusinesses(businesses.filter(business => business.id !== businessId));
        // Also remove associated users from the users state
        setUsers(users.filter(user => user.business_id !== businessId));
      } catch (error) {
        console.error('Error deleting business:', error);
        toast.error('Failed to delete business');
      }
    }
  };

  const handleEditBusiness = (business) => {
    setEditingBusiness(business);
    setBusinessFormData({
      name: business.name,
      email: business.email,
      phone: business.phone || '',
      address: business.address || '',
      emailFrequency: business.email_frequency || 'Quarterly',
      availableCredits: business.available_credits || 0,
      usedCredits: business.used_credits || 0,
      can_run_manual_checks: business.can_run_manual_checks || false
    });
  };

  const handleUpdateBusiness = async (e) => {
    e.preventDefault();
    try {
      // Log the data being sent for debugging
      console.log('Updating business with data:', {
        name: businessFormData.name,
        email: businessFormData.email,
        phone: businessFormData.phone,
        address: businessFormData.address,
        email_frequency: businessFormData.emailFrequency,
        available_credits: parseInt(businessFormData.availableCredits),
        used_credits: parseInt(businessFormData.usedCredits),
        can_run_manual_checks: businessFormData.can_run_manual_checks || false
      });

      // Update business details including credits
      const response = await axiosAuth.put(`/api/admin/businesses/${editingBusiness.id}`, {
        name: businessFormData.name,
        email: businessFormData.email,
        phone: businessFormData.phone,
        address: businessFormData.address,
        email_frequency: businessFormData.emailFrequency,
        available_credits: parseInt(businessFormData.availableCredits),
        used_credits: parseInt(businessFormData.usedCredits),
        can_run_manual_checks: businessFormData.can_run_manual_checks || false
      });

      if (response.data) {
        // Update the businesses state with the new data
        setBusinesses(businesses.map(business => 
          business.id === editingBusiness.id ? response.data : business
        ));
        
        // Reset form and editing state
        setEditingBusiness(null);
        setBusinessFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          emailFrequency: 'Quarterly',
          availableCredits: 0,
          usedCredits: 0,
          can_run_manual_checks: false
        });
      }
    } catch (error) {
      console.error('Error updating business:', error);
      // More detailed error message
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      toast.error(`Failed to update business: ${errorMessage}`);
    }
  };

  const handleBusinessFormChange = (e) => {
    setBusinessFormData({
      ...businessFormData,
      [e.target.name]: e.target.value
    });
  };

  // Add this helper function to group checks by business
  const getChecksByBusiness = () => {
    const grouped = {};
    businesses.forEach(business => {
      const businessChecks = checks.filter(check => check.business_id === business.id);
      // Only count LIVE environment checks for cost
      const liveChecks = businessChecks.filter(check => 
        check.user_environment === 'live' || check.user_environment === 'LIVE'
      );
      grouped[business.id] = {
        checks: businessChecks,
        totalChecks: businessChecks.length,
        liveChecks: liveChecks.length,
        // Calculate cost only for live checks at £0.60 each
        totalCost: (liveChecks.length * 0.60).toFixed(2)
      };
    });
    return grouped;
  };

  // Optional: Add a display formatter function for the UI
  const formatFrequencyDisplay = (frequency) => {
    const displayMap = {
      'Quarterly': 'Quarterly (Every 3 Months)',
      'Biannual': 'Biannual (Every 6 Months)',
      'Yearly': 'Yearly'
    };
    return displayMap[frequency] || frequency;
  };

  // Add this function to fetch staff for a selected business
  const fetchStaffForBusiness = async (businessId) => {
    if (!businessId) {
      setStaffList([]);
      return;
    }
    
    try {
      const response = await axiosAuth.get(`/api/admin/businesses/${businessId}/staff`);
      if (response.data) {
        setStaffList(response.data);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      setStaffList([]);
    }
  };

  // Add this function to handle business selection change
  const handleBusinessChange = (e) => {
    const businessId = e.target.value;
    setSelectedBusinessId(businessId);
    setSelectedStaffId(''); // Reset staff selection
    fetchStaffForBusiness(businessId);
  };

  // Add this function to create a test notification
  const createTestNotification = async (e) => {
    e.preventDefault();
    
    if (!selectedBusinessId) {
      setNotificationResult({
        success: false,
        message: 'Please select a business'
      });
      return;
    }
    
    try {
      setNotificationLoading(true);
      setNotificationResult(null);
      
      const response = await axiosAuth.post('/api/admin/notifications/test', {
        businessId: selectedBusinessId,
        staffId: selectedStaffId || undefined,
        type: notificationType
      });
      
      setNotificationResult({
        success: true,
        message: `Test notification created successfully: ${response.data.message || ''}`
      });
      
    } catch (error) {
      console.error('Error creating test notification:', error);
      setNotificationResult({
        success: false,
        message: error.response?.data?.error || 'Failed to create notification'
      });
    } finally {
      setNotificationLoading(false);
    }
  };

  // Add a separate function for adding credits
  const handleAddCredits = async (amount) => {
    try {
      const newTotal = parseInt(businessFormData.availableCredits) + amount;
      setBusinessFormData({
        ...businessFormData,
        availableCredits: newTotal
      });
    } catch (error) {
      console.error('Error adding credits:', error);
      toast.error('Failed to add credits: ' + error.message);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>System Administration</h1>
        <button onClick={onLogout} className="logout-btn">Logout</button>
      </header>

      <nav className="admin-nav">
        <button 
          className={`tab-btn ${activeTab === 'businesses' ? 'active' : ''}`}
          onClick={() => setActiveTab('businesses')}
        >
          Businesses
        </button>
        <button 
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          Reports
        </button>
        <button 
          className={`tab-btn ${activeTab === 'schedules' ? 'active' : ''}`}
          onClick={() => setActiveTab('schedules')}
        >
          Schedules
        </button>
        {/* Temporarily hiding the notifications tab
        <button 
          className={`tab-btn ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          Notifications
        </button>
        */}
      </nav>

      <div className="admin-content">
        {activeTab === 'businesses' ? (
          <div className="businesses-section">
            <h2>Add New Business</h2>
            <form onSubmit={handleAddBusiness} className="add-form">
              <input
                type="text"
                placeholder="Business Name"
                value={newBusiness.name}
                onChange={(e) => setNewBusiness({...newBusiness, name: e.target.value})}
                required
              />
              <input
                type="email"
                placeholder="Business Email"
                value={newBusiness.email}
                onChange={(e) => setNewBusiness({...newBusiness, email: e.target.value})}
                required
              />
              <input
                type="tel"
                placeholder="Phone"
                value={newBusiness.phone}
                onChange={(e) => setNewBusiness({...newBusiness, phone: e.target.value})}
              />
              <textarea
                placeholder="Address"
                value={newBusiness.address}
                onChange={(e) => setNewBusiness({...newBusiness, address: e.target.value})}
              />
              <select
                value={newBusiness.emailFrequency}
                onChange={(e) => setNewBusiness({...newBusiness, emailFrequency: e.target.value})}
                required
              >
                <option value="Quarterly">Quarterly</option>
                <option value="Biannual">Biannual</option>
                <option value="Yearly">Yearly</option>
              </select>
              <div className="form-group">
                <label>Initial Credits</label>
                <div className="credits-input-group">
                  <input
                    type="number"
                    min="0"
                    placeholder="Initial Credits"
                    value={newBusiness.initialCredits}
                    onChange={(e) => setNewBusiness({
                      ...newBusiness, 
                      initialCredits: parseInt(e.target.value) || 0
                    })}
                    required
                  />
                  <div className="quick-credit-buttons">
                    <button 
                      type="button" 
                      onClick={() => setNewBusiness(prev => ({
                        ...prev, 
                        initialCredits: 100
                      }))}
                    >
                      100
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setNewBusiness(prev => ({
                        ...prev, 
                        initialCredits: 500
                      }))}
                    >
                      500
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setNewBusiness(prev => ({
                        ...prev, 
                        initialCredits: 1000
                      }))}
                    >
                      1000
                    </button>
                  </div>
                </div>
              </div>
              <button type="submit">Add Business</button>
            </form>

            {editingBusiness && (
              <div className="edit-form">
                <h3>Edit Business</h3>
                <form onSubmit={handleUpdateBusiness}>
                  <div>
                    <label>Business Name:</label>
                    <input
                      type="text"
                      name="name"
                      value={businessFormData.name}
                      onChange={handleBusinessFormChange}
                      required
                    />
                  </div>
                  <div>
                    <label>Email:</label>
                    <input
                      type="email"
                      name="email"
                      value={businessFormData.email}
                      onChange={handleBusinessFormChange}
                      required
                    />
                  </div>
                  <div>
                    <label>Phone:</label>
                    <input
                      type="tel"
                      name="phone"
                      value={businessFormData.phone}
                      onChange={handleBusinessFormChange}
                    />
                  </div>
                  <div>
                    <label>Address:</label>
                    <textarea
                      name="address"
                      value={businessFormData.address}
                      onChange={handleBusinessFormChange}
                    />
                  </div>
                  <div>
                    <label>Email Frequency:</label>
                    <select
                      name="emailFrequency"
                      value={businessFormData.emailFrequency}
                      onChange={handleBusinessFormChange}
                      required
                    >
                      <option value="Quarterly">Quarterly</option>
                      <option value="Biannual">Biannual</option>
                      <option value="Yearly">Yearly</option>
                    </select>
                  </div>
                  <div className="credits-management">
                    <h4>Credits Overview</h4>
                    <div className="credits-info">
                      <div className="credits-stat">
                        <div className="credits-label">Available Credits</div>
                        <input
                          type="number"
                          name="availableCredits"
                          value={businessFormData.availableCredits}
                          onChange={handleBusinessFormChange}
                          min="0"
                          className="credits-input"
                        />
                      </div>
                      <div className="credits-stat">
                        <div className="credits-label">Used Credits</div>
                        <div className="credits-value">{businessFormData.usedCredits}</div>
                      </div>
                      <div className="credits-stat">
                        <div className="credits-label">Remaining Credits</div>
                        <div className="credits-value">
                          {businessFormData.availableCredits - businessFormData.usedCredits}
                        </div>
                      </div>
                    </div>
                    <div className="credits-actions">
                      <div className="quick-credit-buttons">
                        <button 
                          type="button" 
                          onClick={() => handleAddCredits(100)}
                          className="add-credits-btn"
                        >
                          Add 100
                        </button>
                        <button 
                          type="button" 
                          onClick={() => handleAddCredits(500)}
                          className="add-credits-btn"
                        >
                          Add 500
                        </button>
                        <button 
                          type="button" 
                          onClick={() => handleAddCredits(1000)}
                          className="add-credits-btn"
                        >
                          Add 1000
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={businessFormData.can_run_manual_checks || false}
                        onChange={(e) => setBusinessFormData({
                          ...businessFormData,
                          can_run_manual_checks: e.target.checked
                        })}
                      />
                      Allow Manual Checks
                    </label>
                    <small className="form-text text-muted">
                      If enabled, users can perform on-demand DVLA checks for staff members.
                    </small>
                  </div>
                  <div className="form-buttons">
                    <button type="submit">Save Changes</button>
                    <button type="button" onClick={() => setEditingBusiness(null)}>Cancel</button>
                  </div>
                </form>
              </div>
            )}

            <h2>Existing Businesses</h2>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Email Frequency</th>
                    <th>Available Credits</th>
                    <th>Used Credits</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {businesses.map(business => (
                    <tr key={business.id}>
                      <td data-label="Name">{business.name}</td>
                      <td data-label="Email">{business.email}</td>
                      <td data-label="Phone">{business.phone}</td>
                      <td data-label="Email Frequency">{formatFrequencyDisplay(business.email_frequency)}</td>
                      <td data-label="Available Credits">{business.available_credits}</td>
                      <td data-label="Used Credits">{business.used_credits}</td>
                      <td data-label="Actions">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEditBusiness(business)}
                        >
                          Edit
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteBusiness(business.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeTab === 'users' ? (
          <div className="users-section">
            <h2>Add New User</h2>
            <form onSubmit={handleAddUser} className="add-form">
              <select
                value={newUser.businessId}
                onChange={(e) => setNewUser({...newUser, businessId: e.target.value})}
                required
              >
                <option value="">Select Business</option>
                {businesses.map(business => (
                  <option key={business.id} value={business.id}>
                    {business.name}
                  </option>
                ))}
              </select>
              <input
                type="email"
                placeholder="Email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="First Name"
                value={newUser.firstName}
                onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
              />
              <input
                type="text"
                placeholder="Last Name"
                value={newUser.lastName}
                onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
              />
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                required
              >
                <option value="user">User</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
              <select
                value={newUser.environment}
                onChange={(e) => setNewUser({...newUser, environment: e.target.value})}
                required
              >
                <option value="test">Test Mode</option>
                <option value="live">Live Mode</option>
              </select>
              <button type="submit">Add User</button>
            </form>

            <h2>Existing Users</h2>
            <div className="table-container">
              {editingUser && (
                <div className="edit-form">
                  <h3>Edit User</h3>
                  <form onSubmit={handleUpdate}>
                    <div>
                      <label>Email:</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label>First Name:</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label>Last Name:</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <label>Role:</label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Role</option>
                        <option value="admin">Admin</option>
                        <option value="manager">Manager</option>
                        <option value="user">User</option>
                      </select>
                    </div>
                    <div>
                      <label>Business:</label>
                      <select
                        name="businessId"
                        value={formData.businessId}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Business</option>
                        {businesses.map(business => (
                          <option key={business.id} value={business.id}>
                            {business.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label>Environment:</label>
                      <select
                        name="environment"
                        value={formData.environment}
                        onChange={handleChange}
                        required
                      >
                        <option value="test">Test Mode</option>
                        <option value="live">Live Mode</option>
                      </select>
                    </div>
                    <div className="form-buttons">
                      <button type="submit">Save Changes</button>
                      <button type="button" onClick={() => setEditingUser(null)}>Cancel</button>
                    </div>
                  </form>
                </div>
              )}
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Business</th>
                    <th>Environment</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td data-label="Name">{`${user.first_name || ''} ${user.last_name || ''}`}</td>
                      <td data-label="Email">{user.email}</td>
                      <td data-label="Role">{user.role}</td>
                      <td data-label="Business">{businesses.find(b => b.id === user.business_id)?.name}</td>
                      <td data-label="Environment">
                        <span className={`environment-badge ${user.environment || 'test'}`}>
                          {(user.environment || 'test').toUpperCase()}
                        </span>
                      </td>
                      <td data-label="Actions">
                        {user.role !== 'owner' && (
                          <>
                            <button 
                              className="edit-btn"
                              onClick={() => handleEdit(user)}
                            >
                              Edit
                            </button>
                            <button 
                              className="delete-btn"
                              onClick={() => handleDelete(user.id)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeTab === 'reports' ? (
          <div className="reports-section">
            <h2>DVLA Check Reports</h2>
            {businesses.map(business => {
              const businessData = getChecksByBusiness()[business.id] || { checks: [], totalChecks: 0, liveChecks: 0, totalCost: 0 };
              return (
                <div key={business.id} className="business-checks">
                  <h3>{business.name}</h3>
                  <div className="business-summary">
                    <p>Total Checks: {businessData.totalChecks}</p>
                    <p>Live Checks: {businessData.liveChecks}</p>
                    <p>Total Cost: £{businessData.totalCost}</p>
                  </div>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Check Type</th>
                          <th>License Number</th>
                          <th>Staff Member</th>
                          <th>Environment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {businessData.checks.map(check => {
                          const staff = users.find(u => u.id === check.staff_id);
                          return (
                            <tr key={check.id}>
                              <td>{new Date(check.check_date).toLocaleString()}</td>
                              <td>{check.check_type}</td>
                              <td>{check.driving_license_number}</td>
                              <td>{staff ? `${staff.first_name} ${staff.last_name}` : 'System'}</td>
                              <td>
                                <span className={`environment-badge ${check.user_environment || 'error'}`}>
                                  {(check.user_environment || 'ERROR').toUpperCase()}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        ) : activeTab === 'schedules' ? (
          <div className="schedules-section">
            <h2>Automated Check Schedules</h2>
            <p className="section-description">
              Configure automated license check schedules for each business. Schedules run hourly and check if any business is due for automated checks.
            </p>
            
            <div className="schedules-overview">
              <table className="schedules-table">
                <thead>
                  <tr>
                    <th>Business</th>
                    <th>Environment</th>
                    <th>Enabled</th>
                    <th>Frequency</th>
                    <th>Check Time</th>
                    <th>Recipients</th>
                    <th>Last Check</th>
                    <th>Next Check</th>
                    <th>Email Settings</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map(schedule => (
                    <tr key={schedule.id}>
                      <td>{schedule.business_name}</td>
                      <td>
                        <span className={`environment-badge ${schedule.environment}`}>
                          {schedule.environment.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${schedule.enabled ? 'enabled' : 'disabled'}`}>
                          {schedule.enabled ? 'ENABLED' : 'DISABLED'}
                        </span>
                      </td>
                      <td>{schedule.frequency}</td>
                      <td>{schedule.check_time ? schedule.check_time.slice(0, 5) : '02:00'}</td>
                      <td>
                        <div className="recipient-emails">
                          {schedule.recipient_emails ? (
                            <span className="email-list" title={schedule.recipient_emails}>
                              {schedule.recipient_emails.split(',').length} email(s)
                            </span>
                          ) : (
                            <span className="default-email" title={schedule.business_email}>
                              Business email
                            </span>
                          )}
                        </div>
                      </td>
                      <td>{schedule.last_check_date ? new Date(schedule.last_check_date).toLocaleString() : 'Never'}</td>
                      <td>{schedule.next_check_date ? new Date(schedule.next_check_date).toLocaleString() : 'N/A'}</td>
                      <td>
                        <div className="email-settings">
                          {schedule.email_on_changes && <span className="email-badge">Changes</span>}
                          {schedule.email_on_no_changes && <span className="email-badge">No Changes</span>}
                          {!schedule.email_on_changes && !schedule.email_on_no_changes && <span className="email-badge disabled">None</span>}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="edit-btn"
                            onClick={() => setEditingSchedule(schedule)}
                          >
                            Edit
                          </button>
                          <button 
                            className={`run-now-btn ${triggeringSchedules.has(schedule.id) ? 'loading' : ''}`}
                            disabled={triggeringSchedules.has(schedule.id)}
                            onClick={async () => {
                              try {
                                console.log(`🚀 Triggering check for schedule ${schedule.id} (${schedule.business_name})`);
                                
                                // Mark as triggering
                                setTriggeringSchedules(prev => new Set(prev).add(schedule.id));
                                
                                // Trigger the check
                                const triggerResponse = await axiosAuth.post(`/api/schedules/admin/trigger/${schedule.id}`);
                                console.log('✅ Backend trigger response:', triggerResponse.data);
                                toast.success(`🚀 Check started for ${schedule.business_name} (${schedule.environment})`);
                                
                                // Scroll to active runs section immediately (showing initializing state)
                                setTimeout(() => {
                                  const activeRunsSection = document.querySelector('.active-runs-section');
                                  if (activeRunsSection) {
                                    activeRunsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }
                                }, 100); // Small delay to allow section to render
                                
                                // Wait 2 seconds before starting to poll (give backend time to create run record)
                                console.log('⏳ Waiting 2 seconds before polling for run record...');
                                setTimeout(async () => {
                                  console.log('🔍 Starting to poll for run record...');
                                  
                                  // Poll for the new run to appear (retry up to 15 times over 15 seconds)
                                  let attempts = 0;
                                  const maxAttempts = 15;
                                  const pollInterval = setInterval(async () => {
                                    attempts++;
                                    console.log(`🔍 Polling attempt ${attempts}/${maxAttempts}...`);
                                    
                                    try {
                                      const runsRes = await axiosAuth.get('/api/schedules/admin/recent-runs?limit=50');
                                      const newRuns = runsRes.data;
                                      
                                      console.log(`📊 Found ${newRuns.length} total runs`);
                                      
                                      // Check if we have a new run for this schedule (even if it completed quickly)
                                      const matchingRun = newRuns.find(run => run.schedule_id === schedule.id);
                                      console.log(`🔎 Looking for schedule_id=${schedule.id}, found:`, matchingRun ? `Run ${matchingRun.id} (${matchingRun.status})` : 'none');
                                      
                                      // If we found a run for this schedule, check if it's recent (within last 30 seconds)
                                      let hasNewRun = false;
                                      if (matchingRun) {
                                        const runDate = new Date(matchingRun.run_date || matchingRun.created_at);
                                        const now = new Date();
                                        const ageInSeconds = (now - runDate) / 1000;
                                        
                                        console.log(`📅 Run age: ${ageInSeconds.toFixed(1)} seconds`);
                                        
                                        // Accept the run if it's less than 30 seconds old OR still running
                                        hasNewRun = ageInSeconds < 30 || 
                                                   matchingRun.status === 'running' || 
                                                   matchingRun.status === 'pending' || 
                                                   !matchingRun.completed_at;
                                        
                                        if (hasNewRun) {
                                          console.log(`✅ Found matching run: ID=${matchingRun.id}, status=${matchingRun.status}, age=${ageInSeconds.toFixed(1)}s`);
                                        } else {
                                          console.log(`⚠️ Run found but too old (${ageInSeconds.toFixed(1)}s)`);
                                        }
                                      }
                                      
                                      // Always update the runs list
                                      setScheduleRuns(newRuns);
                                      
                                      // If we found the run, OR we've tried too many times, stop polling
                                      if (hasNewRun || attempts >= maxAttempts) {
                                        clearInterval(pollInterval);
                                        
                                        if (hasNewRun) {
                                          console.log('🎉 Run found! Removing triggering state.');
                                        } else {
                                          console.warn('⚠️ Run did not appear after 15 attempts');
                                          toast.error('Run started but taking longer than expected to appear. Please refresh if it doesn\'t show up.');
                                        }
                                        
                                        setTriggeringSchedules(prev => {
                                          const next = new Set(prev);
                                          next.delete(schedule.id);
                                          return next;
                                        });
                                      }
                                    } catch (err) {
                                      console.error('❌ Error polling for new run:', err);
                                      if (attempts >= maxAttempts) {
                                        clearInterval(pollInterval);
                                        toast.error('Failed to track run progress. Please refresh to see status.');
                                        setTriggeringSchedules(prev => {
                                          const next = new Set(prev);
                                          next.delete(schedule.id);
                                          return next;
                                        });
                                      }
                                    }
                                  }, 1000); // Poll every 1 second
                                }, 2000); // Wait 2 seconds before starting to poll
                                
                              } catch (error) {
                                console.error('❌ Failed to trigger check:', error);
                                toast.error('Failed to trigger check: ' + (error.response?.data?.error || error.message));
                                setTriggeringSchedules(prev => {
                                  const next = new Set(prev);
                                  next.delete(schedule.id);
                                  return next;
                                });
                              }
                            }}
                            title="Run check now (for testing)"
                          >
                            {triggeringSchedules.has(schedule.id) ? '⏳ Starting...' : '▶ Run Now'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {editingSchedule && (
              <div className="edit-schedule-modal">
                <div className="modal-overlay" onClick={() => setEditingSchedule(null)}></div>
                <div className="modal-content">
                  <h3>Edit Schedule: {editingSchedule.business_name} - {editingSchedule.environment.toUpperCase()}</h3>
                  
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={editingSchedule.enabled}
                        onChange={(e) => setEditingSchedule({
                          ...editingSchedule,
                          enabled: e.target.checked
                        })}
                      />
                      Enable Automated Checks
                    </label>
                  </div>

                  <div className="form-group">
                    <label>Check Frequency</label>
                    <select
                      value={editingSchedule.frequency}
                      onChange={(e) => setEditingSchedule({
                        ...editingSchedule,
                        frequency: e.target.value
                      })}
                    >
                      <option value="daily">Daily (Every day)</option>
                      <option value="weekly">Weekly (Every 7 days)</option>
                      <option value="biweekly">Biweekly (Every 2 weeks)</option>
                      <option value="monthly">Monthly (Every 30 days)</option>
                      <option value="quarterly">Quarterly (Every 3 months)</option>
                      <option value="biannually">Biannually (Every 6 months)</option>
                      <option value="yearly">Yearly (Every 12 months)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Check Time (24-hour format)</label>
                    <input
                      type="time"
                      value={editingSchedule.check_time || '02:00'}
                      onChange={(e) => setEditingSchedule({
                        ...editingSchedule,
                        check_time: e.target.value + ':00'
                      })}
                      className="time-input"
                    />
                    <small className="form-help">
                      Time of day when automated checks should run (server time)
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Email Recipients</label>
                    <input
                      type="text"
                      placeholder="email1@example.com, email2@example.com"
                      value={editingSchedule.recipient_emails || ''}
                      onChange={(e) => setEditingSchedule({
                        ...editingSchedule,
                        recipient_emails: e.target.value
                      })}
                      className="email-input"
                    />
                    <small className="form-help">
                      Comma-separated list of email addresses. Leave blank to use business email ({editingSchedule.business_email || 'not set'})
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Email Notifications</label>
                    <div className="checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={editingSchedule.email_on_changes}
                          onChange={(e) => setEditingSchedule({
                            ...editingSchedule,
                            email_on_changes: e.target.checked
                          })}
                        />
                        Send email when changes detected
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={editingSchedule.email_on_no_changes}
                          onChange={(e) => setEditingSchedule({
                            ...editingSchedule,
                            email_on_no_changes: e.target.checked
                          })}
                        />
                        Send email when no changes (confirmation)
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Exclude Staff from Automated Checks</label>
                    <small className="form-help" style={{marginBottom: '10px', display: 'block'}}>
                      Select staff members to exclude from this automated schedule. They won't be checked during scheduled runs.
                    </small>
                    {loadingStaff ? (
                      <div className="loading-staff">Loading staff...</div>
                    ) : availableStaff.length > 0 ? (
                      <div className="staff-exclusion-list">
                        {availableStaff.map(staff => (
                          <label key={staff.id} className="staff-checkbox">
                            <input
                              type="checkbox"
                              checked={editingSchedule.excluded_staff_ids?.includes(staff.id) || false}
                              onChange={(e) => {
                                const currentExcluded = editingSchedule.excluded_staff_ids || [];
                                const newExcluded = e.target.checked
                                  ? [...currentExcluded, staff.id]
                                  : currentExcluded.filter(id => id !== staff.id);
                                
                                setEditingSchedule({
                                  ...editingSchedule,
                                  excluded_staff_ids: newExcluded
                                });
                              }}
                            />
                            <span>{staff.first_name} {staff.last_name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="no-staff">No staff found for this business/environment</div>
                    )}
                    {editingSchedule.excluded_staff_ids?.length > 0 && (
                      <small className="form-help" style={{marginTop: '10px'}}>
                        {editingSchedule.excluded_staff_ids.length} staff member(s) excluded
                      </small>
                    )}
                  </div>

                  <div className="form-buttons">
                    <button
                      onClick={async () => {
                        try {
                          await axiosAuth.put(`/api/schedules/admin/${editingSchedule.id}`, {
                            enabled: editingSchedule.enabled,
                            frequency: editingSchedule.frequency,
                            frequency_value: editingSchedule.frequency_value,
                            email_on_changes: editingSchedule.email_on_changes,
                            email_on_no_changes: editingSchedule.email_on_no_changes,
                            check_time: editingSchedule.check_time || '02:00:00',
                            recipient_emails: editingSchedule.recipient_emails || null,
                            excluded_staff_ids: editingSchedule.excluded_staff_ids && editingSchedule.excluded_staff_ids.length > 0 
                              ? JSON.stringify(editingSchedule.excluded_staff_ids) 
                              : null
                          });
                          
                          // Update local state
                          setSchedules(schedules.map(s => 
                            s.id === editingSchedule.id ? editingSchedule : s
                          ));
                          
                          setEditingSchedule(null);
                          toast.success('Schedule updated successfully');
                          
                          // Refresh data
                          const schedulesRes = await axiosAuth.get('/api/schedules/admin/all');
                          setSchedules(schedulesRes.data);
                        } catch (error) {
                          toast.error('Failed to update schedule');
                        }
                      }}
                      className="save-btn"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setEditingSchedule(null)}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Logs Viewer Modal */}
            {viewingRunLogs && (
              <div className="logs-modal">
                <div className="modal-overlay" onClick={() => setViewingRunLogs(null)}></div>
                <div className="modal-content logs-modal-content">
                  <div className="modal-header">
                    <h3>
                      Run Logs - {runDetails?.business_name} ({runDetails?.environment?.toUpperCase()})
                    </h3>
                    <button className="close-btn" onClick={() => setViewingRunLogs(null)}>×</button>
                  </div>

                  {loadingLogs ? (
                    <div className="loading-logs">Loading logs...</div>
                  ) : (
                    <>
                      {/* Run Summary */}
                      <div className="run-summary">
                        <div className="summary-grid">
                          <div className="summary-item">
                            <label>Status:</label>
                            <span className={`status-badge ${runDetails?.status}`}>
                              {runDetails?.status}
                            </span>
                          </div>
                          <div className="summary-item">
                            <label>Started:</label>
                            <span>{runDetails?.created_at ? new Date(runDetails.created_at).toLocaleString() : 'N/A'}</span>
                          </div>
                          <div className="summary-item">
                            <label>Completed:</label>
                            <span>{runDetails?.completed_at ? new Date(runDetails.completed_at).toLocaleString() : 'In progress'}</span>
                          </div>
                          <div className="summary-item">
                            <label>Total Staff:</label>
                            <span>{runDetails?.total_staff || 0}</span>
                          </div>
                          <div className="summary-item">
                            <label>Processed:</label>
                            <span>{runDetails?.processed_staff || 0}</span>
                          </div>
                          <div className="summary-item">
                            <label>Successful:</label>
                            <span className="success-count">{runDetails?.successful_checks || 0}</span>
                          </div>
                          <div className="summary-item">
                            <label>Failed:</label>
                            <span className="failed-count">{runDetails?.failed_checks || 0}</span>
                          </div>
                          <div className="summary-item">
                            <label>Changes:</label>
                            <span className="changes-count">{runDetails?.changes_detected || 0}</span>
                          </div>
                          <div className="summary-item">
                            <label>Credits Used:</label>
                            <span>{runDetails?.credits_used || 0}</span>
                          </div>
                          {runDetails?.credits_required && (
                            <div className="summary-item">
                              <label>Credits Available:</label>
                              <span>{runDetails?.credits_available || 0} / {runDetails?.credits_required}</span>
                            </div>
                          )}
                        </div>
                        {runDetails?.error_summary && (
                          <div className="error-banner">
                            <strong>⚠️ Error:</strong> {runDetails.error_summary}
                          </div>
                        )}
                      </div>

                      {/* Individual Staff Logs */}
                      <div className="staff-logs-section">
                        <h4>Individual Staff Checks ({runLogs.length})</h4>
                        <div className="logs-list">
                          {runLogs.length === 0 ? (
                            <div className="no-logs">No detailed logs available for this run.</div>
                          ) : (
                            runLogs.map((log, index) => (
                              <div key={log.id} className={`log-entry log-status-${log.status}`}>
                                <div className="log-header">
                                  <div className="log-index">#{index + 1}</div>
                                  <div className="log-staff-name">
                                    <strong>{log.staff_name}</strong>
                                    <span className="log-license">{log.license_number}</span>
                                  </div>
                                  <span className={`log-status-badge ${log.status}`}>
                                    {log.status}
                                  </span>
                                </div>

                                {log.status === 'failed' && log.error_message && (
                                  <div className="log-error">
                                    <span className="error-code">{log.error_code || 'ERROR'}</span>
                                    <span className="error-message">{log.error_message}</span>
                                  </div>
                                )}

                                {log.status === 'success' && (
                                  <div className="log-success">
                                    {log.changes_detected ? (
                                      <span className="changes-indicator">✓ Changes detected</span>
                                    ) : (
                                      <span className="no-changes-indicator">No changes</span>
                                    )}
                                    {log.change_summary && log.changes_detected && (
                                      <div className="change-details">
                                        {(() => {
                                          try {
                                            const changes = JSON.parse(log.change_summary);
                                            return (
                                              <ul>
                                                {changes.map((change, i) => (
                                                  <li key={i}>{change}</li>
                                                ))}
                                              </ul>
                                            );
                                          } catch (e) {
                                            return null;
                                          }
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div className="log-footer">
                                  {log.check_duration_ms && (
                                    <span className="log-duration">
                                      Duration: {(log.check_duration_ms / 1000).toFixed(2)}s
                                    </span>
                                  )}
                                  {log.credits_used !== undefined && (
                                    <span className="log-credits">
                                      Credits: {log.credits_used}
                                    </span>
                                  )}
                                  <span className="log-timestamp">
                                    {log.completed_at ? new Date(log.completed_at).toLocaleTimeString() : 'Processing...'}
                                  </span>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="modal-footer">
                    <button className="close-footer-btn" onClick={() => setViewingRunLogs(null)}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Active Runs Section with Live Progress */}
            {(() => {
              const now = new Date();
              const recentOrActiveRuns = scheduleRuns.filter(run => {
                const isActive = run.status === 'running' || (!run.completed_at && run.status !== 'completed' && run.status !== 'failed');
                if (isActive) return true;
                
                // Also include recently completed runs (within last 30 seconds)
                const runDate = new Date(run.run_date || run.created_at);
                const ageInSeconds = (now - runDate) / 1000;
                return ageInSeconds < 30;
              });
              
              return (recentOrActiveRuns.length > 0 || triggeringSchedules.size > 0);
            })() && (
              <div className="active-runs-section">
                <h3>🔴 Active Runs - Live Progress</h3>
                
                {/* Show "Initializing" message for each triggered schedule that doesn't have a run yet */}
                {Array.from(triggeringSchedules).map(scheduleId => {
                  // Check if this schedule already has a recent run (within last 30 seconds)
                  const now = new Date();
                  const hasRecentRun = scheduleRuns.some(run => {
                    if (run.schedule_id !== scheduleId) return false;
                    
                    const runDate = new Date(run.run_date || run.created_at);
                    const ageInSeconds = (now - runDate) / 1000;
                    
                    return ageInSeconds < 30 || run.status === 'running' || !run.completed_at;
                  });
                  
                  // Only show initializing card if no recent run exists yet for this schedule
                  if (hasRecentRun) return null;
                  
                  const schedule = schedules.find(s => s.id === scheduleId);
                  if (!schedule) return null;
                  
                  return (
                    <div key={scheduleId} className="initializing-run-card">
                      <div className="initializing-content">
                        <div className="spinner"></div>
                        <div className="initializing-text">
                          <strong>🚀 Starting Check: {schedule.business_name}</strong>
                          <p>Connecting to backend and preparing license checks. This usually takes 2-5 seconds.</p>
                          <p style={{fontSize: '12px', color: '#95a5a6', marginTop: '8px'}}>
                            ⏳ Waiting for run to initialize... (Environment: {schedule.environment.toUpperCase()})
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                <div className="active-runs-list">
                  {scheduleRuns
                    .filter(run => {
                      const now = new Date();
                      
                      // Always show running runs
                      if (run.status === 'running') return true;
                      
                      // Show pending runs
                      if (!run.completed_at && run.status !== 'completed' && run.status !== 'failed') return true;
                      
                      // Show completed/failed runs if they happened recently (within last 60 seconds)
                      if (run.status === 'completed' || run.status === 'failed') {
                        // Check completed_at first
                        if (run.completed_at) {
                          const completedDate = new Date(run.completed_at);
                          const ageInSeconds = (now - completedDate) / 1000;
                          return ageInSeconds < 60;
                        }
                        // Fallback to run_date if completed_at not set yet
                        const runDate = new Date(run.run_date || run.created_at);
                        const ageInSeconds = (now - runDate) / 1000;
                        // If run started within last 60 seconds and status is completed, show it
                        return ageInSeconds < 60;
                      }
                      
                      // Default: don't show old runs
                      return false;
                    })
                    .map(run => {
                      const progress = activeRunsProgress[run.id] || {};
                      // CRITICAL: Prioritize progress endpoint data FIRST (it's more accurate)
                      // Progress endpoint queries directly and should have latest data
                      // Then fall back to recent-runs data if progress isn't available
                      const total = (progress.total_staff ?? run.total_staff ?? run.total_staff_checked ?? 0);
                      // Check both run.status and progress.status to detect completion (do this FIRST)
                      // ALWAYS prioritize progress.status as it's queried fresh from the database
                      // If progress.isComplete is true, force status to 'completed' even if status field is missing
                      let runStatus = progress.status || run.status;
                      if (progress.isComplete && !runStatus) {
                        runStatus = 'completed';
                      } else if (progress.isComplete && runStatus !== 'completed' && runStatus !== 'failed') {
                        // If isComplete is true but status doesn't reflect it, use completed
                        runStatus = 'completed';
                      }
                      // Convert to numbers, handling null/undefined
                      const progressProcessed = progress.processed_staff != null ? Number(progress.processed_staff) : null;
                      const runProcessed = run.processed_staff != null ? Number(run.processed_staff) : null;
                      // Use progress data first, then run data, then 0
                      let processed = progressProcessed ?? runProcessed ?? 0;
                      // If completed, ensure we show total
                      if (runStatus === 'completed' && total > 0) {
                        processed = Math.max(processed, total);
                      }
                      const successful = (run.successful_checks ?? progress.successful_checks ?? 0);
                      const failed = (run.failed_staff ?? progress.failed_staff ?? run.failed_checks ?? progress.failed_checks ?? 0);
                      const progressPercent = total > 0 ? Math.round((processed / total) * 100) : 0;
                      const isComplete = progress.isComplete || (runStatus === 'completed' || runStatus === 'failed');
                      
                      // Debug logging for status changes
                      if (run.status !== runStatus) {
                        console.log(`🔄 Run ${run.id} status changed: ${run.status} → ${runStatus}`);
                      }
                      if (run.status === 'running' || runStatus === 'running') {
                        console.log(`🔄 Run ${run.id} progress:`, {
                          runStatus: run.status,
                          progressStatus: progress.status,
                          finalStatus: runStatus,
                          runProcessed: run.processed_staff,
                          progressProcessed: progress.processed_staff,
                          finalProcessed: processed,
                          total,
                          progressPercent,
                          isComplete
                        });
                      }
                      if (runStatus === 'completed' || runStatus === 'failed') {
                        console.log(`✅ Run ${run.id} completed: status=${runStatus}, processed=${processed}/${total}`);
                      }
                      
                      return (
                        <div key={run.id} className={`active-run-card ${runStatus === 'completed' || runStatus === 'failed' ? 'completed' : ''}`}>
                          <div className="active-run-header">
                            <div className="active-run-title">
                              <strong>{run.business_name}</strong>
                              <span className={`environment-badge ${run.environment}`}>
                                {run.environment?.toUpperCase() || 'UNKNOWN'}
                              </span>
                              {(runStatus === 'completed' || runStatus === 'failed') && (
                                <span className={`status-badge ${runStatus}`} style={{
                                  background: runStatus === 'completed' ? '#10b981' : '#ef4444',
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}>
                                  {runStatus === 'completed' ? '✅ COMPLETED' : '❌ FAILED'}
                                </span>
                              )}
                              {runStatus === 'running' && (
                                <span className={`status-badge ${runStatus}`}>
                                  {runStatus.toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="active-run-time">
                              Started: {new Date(run.run_date || run.created_at).toLocaleTimeString()}
                              {(runStatus === 'completed' || runStatus === 'failed') && run.completed_at && (
                                <span style={{marginLeft: '10px', color: '#10b981', fontWeight: 'bold'}}>
                                  • Finished: {new Date(run.completed_at).toLocaleTimeString()}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="progress-bar-container">
                            <div className="progress-bar-header">
                              <span className="progress-label">
                                Processing: {processed} / {total} staff members
                              </span>
                              <span className="progress-percent">{progressPercent}%</span>
                            </div>
                            <div className="progress-bar-wrapper">
                              <div 
                                className="progress-bar-fill"
                                style={{ width: `${progressPercent}%` }}
                              >
                                <div className="progress-bar-shimmer"></div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="active-run-stats">
                            <div className="stat-item success-stat">
                              <span className="stat-label">✅ Successful:</span>
                              <span className="stat-value">{successful}</span>
                            </div>
                            {failed > 0 && (
                              <div className="stat-item failed-stat">
                                <span className="stat-label">❌ Failed:</span>
                                <span className="stat-value">{failed}</span>
                              </div>
                            )}
                            <div className="stat-item">
                              <span className="stat-label">💰 Credits Used:</span>
                              <span className="stat-value">{progress.credits_used || run.credits_used || 0}</span>
                            </div>
                            {total > 0 && (
                              <div className="stat-item">
                                <span className="stat-label">⏱️ Est. Remaining:</span>
                                <span className="stat-value">
                                  {isComplete ? 'Complete' : processed > 0 ? `${Math.round((total - processed) * 1.5)}s` : 'Calculating...'}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {progress.recentLogs && progress.recentLogs.length > 0 && (
                            <div className="active-run-recent-logs">
                              <div className="recent-logs-label">Latest:</div>
                              {progress.recentLogs.slice(0, 3).map((log, idx) => (
                                <div key={idx} className={`recent-log-entry ${log.status}`}>
                                  <span className="log-staff">{log.staff_name}</span>
                                  <span className={`log-status ${log.status}`}>
                                    {log.status === 'success' ? '✅' : log.status === 'failed' ? '❌' : '⏳'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {run.error_summary && (
                            <div className="active-run-error">
                              ⚠️ {run.error_summary}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            <div className="schedule-runs-section">
              <h3>Recent Automated Check Runs</h3>
              <table className="runs-table">
                <thead>
                  <tr>
                    <th>Business</th>
                    <th>Environment</th>
                    <th>Run Date</th>
                    <th>Status</th>
                    <th>Progress</th>
                    <th>Changes</th>
                    <th>Credits</th>
                    <th>Email</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleRuns.map(run => {
                    const progress = activeRunsProgress[run.id];
                    const isRunning = run.status === 'running' || (!run.completed_at && run.status !== 'completed' && run.status !== 'failed');
                    const total = progress?.total_staff || run.total_staff || run.total_staff_checked || 0;
                    const processed = progress?.processed_staff || run.processed_staff || (run.status === 'completed' ? run.total_staff_checked : 0);
                    const progressPercent = total > 0 ? Math.round((processed / total) * 100) : 0;
                    
                    return (
                    <tr key={run.id} className={isRunning ? 'running-row' : ''}>
                      <td>{run.business_name}</td>
                      <td>
                        <span className={`environment-badge ${run.environment}`}>
                          {run.environment.toUpperCase()}
                        </span>
                      </td>
                      <td>{new Date(run.run_date || run.created_at).toLocaleString()}</td>
                      <td>
                        <div className="status-cell">
                          <span className={`status-badge ${run.status} ${isRunning ? 'pulsing' : ''}`}>
                            {isRunning ? '🔄 RUNNING' : run.status}
                          </span>
                          {run.error_summary && (
                            <div className="error-summary" title={run.error_summary}>
                              ⚠️ {run.error_summary}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="progress-info">
                          {isRunning ? (
                            <div className="live-progress-container">
                              <div className="progress-bar-header-small">
                                <span>{processed} / {total}</span>
                                <span className="progress-percent-small">{progressPercent}%</span>
                              </div>
                              <div className="progress-bar-wrapper-small">
                                <div 
                                  className="progress-bar-fill-small"
                                  style={{ width: `${progressPercent}%` }}
                                >
                                  <div className="progress-bar-shimmer"></div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="progress-text">
                                {run.successful_checks || 0} / {run.total_staff_checked || run.total_staff || 0}
                              </div>
                              {run.failed_checks > 0 && (
                                <div className="failed-count">({run.failed_checks} failed)</div>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        {run.changes_detected > 0 ? (
                          <span className="changes-badge">{run.changes_detected}</span>
                        ) : (
                          <span className="no-changes">0</span>
                        )}
                      </td>
                      <td>
                        <div className="credits-info">
                          <div>{run.credits_used || 0} used</div>
                          {run.credits_required && run.credits_available !== undefined && (
                            <div className="credits-detail">
                              ({run.credits_available} / {run.credits_required})
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="email-status-cell">
                          {run.email_sent ? (
                            <span className="email-sent-badge" title="Email sent successfully">
                              ✅ Email Sent
                            </span>
                          ) : run.status === 'completed' || run.status === 'failed' ? (
                            <span className="email-not-sent-badge" title="Email not sent">
                              ❌ Not Sent
                            </span>
                          ) : (
                            <span className="email-pending-badge" title="Email will be sent when complete">
                              ⏳ Pending
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          className="view-logs-btn"
                          onClick={() => handleViewLogs(run.id)}
                          title="View detailed logs"
                        >
                          📋 View Logs
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="notifications-section">
            <h2>Notification Tester</h2>
            <p className="section-description">
              Create test notifications for any business to verify the notification system.
            </p>
            
            <form onSubmit={createTestNotification} className="notification-test-form">
              <div className="form-group">
                <label>Select Business:</label>
                <select 
                  value={selectedBusinessId} 
                  onChange={handleBusinessChange}
                  required
                >
                  <option value="">Select a Business</option>
                  {businesses.map(business => (
                    <option key={business.id} value={business.id}>
                      {business.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Notification Type:</label>
                <select 
                  value={notificationType} 
                  onChange={(e) => setNotificationType(e.target.value)}
                  required
                >
                  <option value="SYSTEM_UPDATE">System Update</option>
                  <option value="CHECK_LIMIT_WARNING">Check Limit Warning</option>
                  <option value="LICENSE_STATUS_CHANGE">License Status Change</option>
                  <option value="LICENSE_EXPIRING">License Expiring</option>
                  <option value="POINTS_CHANGE">Points Change</option>
                  <option value="NEW_STAFF">New Staff</option>
                  <option value="NEW_ENDORSEMENT">New Endorsement</option>
                  <option value="INACTIVE_STAFF">Inactive Staff</option>
                  <option value="HIGH_PRIORITY">High Priority Alert</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Staff Member (optional):</label>
                <select 
                  value={selectedStaffId} 
                  onChange={(e) => setSelectedStaffId(e.target.value)}
                >
                  <option value="">None (System Notification)</option>
                  {staffList.map(staff => (
                    <option key={staff.id} value={staff.id}>
                      {staff.first_name} {staff.last_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <button 
                type="submit" 
                className="create-notification-btn"
                disabled={notificationLoading || !selectedBusinessId}
              >
                {notificationLoading ? 'Creating...' : 'Create Test Notification'}
              </button>
              
              {notificationResult && (
                <div className={`notification-result ${notificationResult.success ? 'success' : 'error'}`}>
                  {notificationResult.message}
                </div>
              )}
            </form>
            
            <div className="notification-info">
              <h3>Notification Types</h3>
              <ul className="notification-types-list">
                <li><strong>System Update</strong> - General system announcements</li>
                <li><strong>Check Limit Warning</strong> - Alerts when approaching monthly check limits</li>
                <li><strong>License Status Change</strong> - When a driver's license status changes</li>
                <li><strong>License Expiring</strong> - When a license is about to expire</li>
                <li><strong>Points Change</strong> - When penalty points change on a license</li>
                <li><strong>New Staff</strong> - When a new staff member is added</li>
                <li><strong>New Endorsement</strong> - When a new endorsement appears on a license</li>
                <li><strong>Inactive Staff</strong> - For staff with no recent license checks</li>
                <li><strong>High Priority</strong> - Critical alerts requiring immediate attention</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel; 